<template>
  <Card class="task-card cursor-pointer" @click="handleCardClick">
    <template #content>
      <!-- Mobile layout: stacked -->
      <div class="flex flex-col gap-3 px-2 py-2 md:hidden">
        <!-- Selection checkbox -->
        <div v-if="!readOnly" class="flex items-center gap-2">
          <Checkbox :modelValue="selected" @update:modelValue="$emit('select', task.id)" binary />
          <span class="text-sm text-gray-600">Select task</span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex flex-col gap-1">
            <div class="font-medium text-lg">{{ task.title }}</div>
            <div v-if="task.personName || task.organizationName" class="flex items-center gap-2">
              <div
                v-if="task.personName"
                class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                @click.stop="$emit('click:person', task.personId)"
              >
                <i class="pi pi-user"></i>
                <span>{{ task.personName }}</span>
              </div>
              <div
                v-if="task.organizationName"
                class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                @click.stop="$emit('click:organization', task.organizationId)"
              >
                <i class="pi pi-building"></i>
                <span>{{ task.organizationName }}</span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Tag :value="getStatusLabel(task.status)" :severity="getStatusSeverity(task.status)" />
            <Button
              v-if="!readOnly"
              icon="pi pi-ellipsis-v"
              size="small"
              text
              rounded
              @click.stop="toggleMenu"
              aria-haspopup="true"
              aria-controls="action_menu"
            />
          </div>
        </div>

        <div v-if="task.description" class="text-sm text-gray-600 line-clamp-2">
          {{ task.description }}
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              v-if="task.dueAt && task.status !== 'done'"
              class="text-sm"
              :class="getDueDateClass()"
            >
              {{ formatDueDate(task.dueAt) }}
            </span>
            <span v-if="task.assignedTo" class="text-sm text-gray-500">
              <i class="pi pi-user mr-1"></i>{{ task.assignedTo }}
            </span>
          </div>
        </div>

        <Menu ref="menu" id="action_menu" :model="actionItems" :popup="true" />
      </div>

      <!-- Desktop layout: table row -->
      <div class="hidden md:flex items-start px-2 py-2 gap-6">
        <!-- Selection checkbox -->
        <div v-if="!readOnly" class="flex items-center min-w-[40px]">
          <Checkbox :modelValue="selected" @update:modelValue="$emit('select', task.id)" binary />
        </div>
        <!-- Title and Entity Links -->
        <div class="flex-1 min-w-[200px]">
          <div class="font-medium">{{ task.title }}</div>
          <div v-if="task.personName || task.organizationName" class="flex items-center gap-2 mt-1">
            <div
              v-if="task.personName"
              class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
              @click.stop="$emit('click:person', task.personId)"
            >
              <i class="pi pi-user"></i>
              <span class="truncate">{{ task.personName }}</span>
            </div>
            <div
              v-if="task.organizationName"
              class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
              @click.stop="$emit('click:organization', task.organizationId)"
            >
              <i class="pi pi-building"></i>
              <span class="truncate">{{ task.organizationName }}</span>
            </div>
          </div>
        </div>

        <!-- Due Date -->
        <div v-if="task.status !== 'done'" class="text-sm min-w-[120px]" :class="getDueDateClass()">
          {{ task.dueAt ? formatDueDate(task.dueAt) : 'No due date' }}
        </div>

        <!-- Assigned To -->
        <div class="text-sm text-gray-500 min-w-[100px]">
          {{ task.assignedTo || 'Unassigned' }}
        </div>

        <!-- Status Tag -->
        <div class="min-w-[100px]">
          <Tag :value="getStatusLabel(task.status)" :severity="getStatusSeverity(task.status)" />
        </div>

        <!-- Actions -->
        <div v-if="!readOnly" class="min-w-[120px] mt-1">
          <Button
            v-if="task.status === 'todo'"
            icon="pi pi-play"
            size="small"
            outlined
            v-tooltip="'Begin'"
            class="mr-2"
            severity="info"
            @click.stop="$emit('begin', task)"
          />
          <Button
            icon="pi pi-check"
            size="small"
            outlined
            v-tooltip="'Complete'"
            class="mr-2"
            @click.stop="$emit('complete', task)"
          />
          <Button
            icon="pi pi-pencil"
            size="small"
            outlined
            v-tooltip="'Edit'"
            class="mr-2"
            severity="warn"
            @click.stop="$emit('edit', task)"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            outlined
            v-tooltip="'Delete'"
            severity="danger"
            @click.stop="$emit('delete', task)"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatDate } from '@/utils/formatters'
import type { Task } from '@/types/Task'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Menu from 'primevue/menu'
import Checkbox from 'primevue/checkbox'

const props = defineProps<{
  task: Task
  readOnly?: boolean
  selected?: boolean
}>()

const emit = defineEmits<{
  select: [taskId: string]
  view: [task: Task]
  begin: [task: Task]
  complete: [task: Task]
  edit: [task: Task]
  delete: [task: Task]
  'click:person': [personId?: string]
  'click:organization': [organizationId?: string]
}>()

const menu = ref()

// Action menu items
const actionItems = computed(() => [
  {
    label: 'View',
    icon: 'pi pi-eye',
    command: () => emit('view', props.task),
  },
  {
    label: 'Edit',
    icon: 'pi pi-pencil',
    command: () => emit('edit', props.task),
  },
  {
    separator: true,
  },
  {
    label: 'Delete',
    icon: 'pi pi-trash',
    command: () => emit('delete', props.task),
  },
])

// Helper methods
const getStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    todo: 'To Do',
    doing: 'In Progress',
    done: 'Done',
    cancelled: 'Cancelled',
  }
  return map[status] || status
}

const getStatusSeverity = (status: string): string => {
  const map: Record<string, string> = {
    todo: 'info',
    doing: 'warning',
    done: 'success',
    cancelled: 'danger',
  }
  return map[status] || 'info'
}

const formatDueDate = (dueAt: Date | string): string => {
  const date = new Date(dueAt)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dueDate = new Date(date)
  dueDate.setHours(0, 0, 0, 0)

  if (dueDate < today) {
    return `Overdue (${formatDate(dueAt)})`
  } else if (dueDate.getTime() === today.getTime()) {
    return 'Due Today'
  } else {
    return formatDate(dueAt)
  }
}

const getDueDateClass = (): string => {
  if (!props.task.dueAt) return 'text-gray-500'

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dueDate = new Date(props.task.dueAt)
  dueDate.setHours(0, 0, 0, 0)

  if (dueDate < today) {
    return 'text-red-600 font-medium'
  } else if (dueDate.getTime() === today.getTime()) {
    return 'text-orange-600 font-medium'
  } else {
    return 'text-gray-500'
  }
}

const handleCardClick = () => {
  emit('view', props.task)
}

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}
</script>

<style scoped>
.task-card :deep(.p-card) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition:
    box-shadow 0.2s,
    transform 0.2s;
}

.task-card :deep(.p-card:hover) {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
